import { useState, useEffect, useRef, useMemo } from 'react'
import { supabase } from '../../../services/supabaseClient'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { Badge } from '../../../components/ui/badge'
import { useToast } from '../../../hooks/use-toast'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { fetchPlatformConfig } from '../lib/platformConfig'
import { Separator } from '../../../components/ui/separator'
import { Textarea } from '../../../components/ui/textarea'
import {
  getMqttAdminUrl,
  fetchGatewayConfig,
  saveGatewayConfig,
  updateGatewayLastSeen,
  probeGateway,
  buildGetCfgCommand,
  resolveGatewayMqttTopics,
  sendMqttAdminCommand,
} from '../gatewayAdminApi'
import { getGatewayProductFamily } from '../../../utils/gatewayProductLabel'
import { getGatewayAdminCapabilities } from '../../../utils/gatewayAdminCapabilities'
import GatewayTypeTags from '../../../components/GatewayTypeTags'
import GatewayCompactConnectBar from './GatewayCompactConnectBar'
import EspFirmwareOtaPanel from './EspFirmwareOtaPanel'
import { Button as CarbonButton, TextInput, Tile, Dropdown, Modal } from '@carbon/react'
import { 
  Power, 
  RotateCcw, 
  Key, 
  Wifi, 
  Bluetooth, 
  Cloud, 
  Settings, 
  Download,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Send,
  ChevronDown
} from 'lucide-react'

const getMqttBridgeUrl = () => getMqttAdminUrl()

const COMMAND_OPTIONS = [
  { id: 'get_status', text: 'Get Current Configuration' },
  { id: 'set_upload_interval', text: 'Set Upload Interval' },
  { id: 'set_rssi_filter', text: 'Set RSSI Filter' },
  { id: 'set_mac_filter', text: 'Set MAC Filter' },
  { id: 'set_name_filter', text: 'Set BLE Name Filter' },
  { id: 'set_beacon_types', text: 'Set Beacon Types (iBeacon/Eddystone/etc)' },
  { id: 'set_duplicate_filter', text: 'Set Duplicate Filter' },
  { id: 'reboot', text: 'Reboot Gateway' },
  { id: 'factory_reset', text: 'Factory Reset' },
  { id: 'change_password', text: 'Change Admin Password' },
  { id: 'update_ble_config', text: 'Update BLE Configuration' },
  { id: 'update_network_config', text: 'Update Network Configuration' },
  { id: 'update_mqtt_config', text: 'Update MQTT Configuration' },
  { id: 'update_http_config', text: 'Update HTTP Configuration' },
  { id: 'configure_ap', text: 'Configure Access Point' },
  { id: 'update_firmware', text: 'Firmware Update' },
]

// Default JSON payloads for each command type
// Based on KGateway API documentation and working script
const COMMAND_TEMPLATES = {
  set_upload_interval: {
    msg: 'admin',
    stype: 'cfg',
    rptPeriod: '90'  // Upload period in seconds (correct parameter name)
  },
  set_rssi_filter: {
    msg: 'admin',
    stype: 'cfg',
    filterrssi: '-85'  // RSSI filter in dBm (correct parameter name)
  },
  set_mac_filter: {
    msg: 'admin',
    stype: 'cfg',
    filtermac: '^28:2C|^AA:BB'  // Regex pattern (correct parameter name)
  },
  set_name_filter: {
    msg: 'admin',
    stype: 'cfg',
    filterdata1: '^KBPro|^KBeacon'  // Regex pattern for BLE name/data
  },
  set_beacon_types: {
    msg: 'admin',
    stype: 'cfg',
    rptiBeacon: 1,      // Report iBeacon (correct parameter name)
    rptEddystone: 1,    // Report Eddystone (correct parameter name)
    rptSensor: 1,       // Report KSensor (correct parameter name)
    rptProximity: 1,    // Report Proximity (correct parameter name)
    rptUnknown: 1       // Report Unknown (correct parameter name)
  },
  set_duplicate_filter: {
    msg: 'admin',
    stype: 'cfg',
    filterDuplicate: '2'  // Filter by mac+type (correct parameter name)
  },
  reboot: {
    msg: 'admin',
    stype: 'reboot'  // Reboot command
  },
  get_status: {
    msg: 'admin',
    stype: 'getCfg',  // Use getCfg to retrieve current configuration
    seq: 1  // Will be overridden with actual sequence number
  },
  update_mqtt_config: {
    msg: 'admin',
    stype: 'cfg',
    ctype: 'MQTT',
    murl: '',
    mport: '1883',
    username: '',
    password: '',
    clientid: '',
    pubtopic: '',
    subtopic: '',
    pubaction: '',
    subadmintopic: '',
    pubadmintopic: '',
    qos: '0',
    aliveTopic: '0'
  },
  update_ble_config: {
    msg: 'admin',
    stype: 'cfg',
    actScan: '0',        // Active scanning
    scanMode: '0',       // BLE legacy scanning
    rssionly: '0'       // Report complete advertisement
  },
  update_gps_config: {
    msg: 'admin',
    stype: 'cfg',
    rptGpsLocation: '1',    // Enable GPS location report
    gpsLocationPeriod: '60', // GPS positioning period (seconds)
    rptGpsInAdvMsg: '0'      // GPS location in advertisement messages
  },
  factory_reset: {
    msg: 'admin',
    stype: 'factory',
    confirm: 1  // Must be 1 to confirm factory reset
  },
  change_password: {
    msg: 'admin',
    stype: 'password',
    oldPassword: '0000000000000000',  // Current admin password (16 ASCII 0s default)
    newPassword: 'NewPassword123'       // New admin password (min 5 chars)
  },
  update_network_config: {
    msg: 'admin',
    stype: 'network',
    ssid: 'YourWiFiName',              // WiFi SSID
    password: 'YourWiFiPassword',      // WiFi password
    encryption: 'psk2',                 // Encryption (none, psk, psk2)
    dhcp: 1,                           // Use DHCP (0=NO, 1=YES)
    staticIp: '',                      // Static IP (if dhcp=0)
    staticMask: '',                    // Subnet mask (if dhcp=0)
    staticGateway: '',                 // Gateway IP (if dhcp=0)
    staticDns1: '',                    // Primary DNS (if dhcp=0)
    staticDns2: ''                     // Secondary DNS (if dhcp=0)
  },
  update_http_config: {
    msg: 'admin',
    stype: 'http',
    ctype: 'HTTP',
    httpUrl: 'https://your-api.com/gateway-data',  // HTTP POST URL
    httpHeader: 'Authorization: Bearer your_token', // HTTP headers
    httpMaxPktSize: 100,                         // Max packet size
    httpPeriod: '60'                               // Upload period in seconds
  },
  configure_ap: {
    msg: 'admin',
    stype: 'ap',
    apSsid: 'GatewayAP',               // Access Point SSID
    apPassword: 'password123',         // AP password (min 8 chars)
    apHidden: 0,                       // Hidden SSID (0=NO, 1=YES)
    apPowerOff: 0,                     // Turn off AP (0=NO, 1=YES)
    apLanIp: '192.168.8.1'             // AP LAN IP
  },
  update_firmware: {
    msg: 'admin',
    stype: 'firmware',
    fwUrl: 'https://example.com/firmware/gateway-v2.1.0.bin',  // Firmware download URL
    fwVersion: '2.1.0',                                        // Firmware version
    fwChecksum: '',                                            // SHA256 checksum (optional)
    fwForce: 0                                                 // Force update (0=NO, 1=YES)
  }
}

// MQTT connection state

export default function GatewayAdminView({
  configOnly = false,
  initialGatewayId = null,
  initialGateway = null,
  lockGateway = false,
}) {
  const { toast } = useToast()
  
  // User and gateway states
  const [user, setUser] = useState(null)
  const [gateways, setGateways] = useState([])
  const [gatewayStatuses, setGatewayStatuses] = useState({}) // {gatewayId: {is_online, last_seen, testing}}
  const [selectedGateway, setSelectedGateway] = useState(null)
  const [loadingGateways, setLoadingGateways] = useState(false)
  const [loadingStatuses, setLoadingStatuses] = useState(false)
  const [mqttClient, setMqttClient] = useState(null)
  const [mqttConnectionInfo, setMqttConnectionInfo] = useState(null)
  const [connected, setConnected] = useState(false)
  const [adminMessages, setAdminMessages] = useState([])
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState(null)

  // Command states
  const [sendingCommand, setSendingCommand] = useState(false)
  const [commandHistory, setCommandHistory] = useState([])
  const [commandSequence, setCommandSequence] = useState(1) // Sequence number for gateway API

  // Form states for different commands
  const [newPassword, setNewPassword] = useState('')
  const [firmwareUrl, setFirmwareUrl] = useState('')
  const [firmwareVersion, setFirmwareVersion] = useState('')
  
  // Command modal states
  const [showCommandModal, setShowCommandModal] = useState(false)
  const [selectedCommandType, setSelectedCommandType] = useState('')
  const [commandConfig, setCommandConfig] = useState({})
  const [currentGatewayConfig, setCurrentGatewayConfig] = useState(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [commandJsonPayload, setCommandJsonPayload] = useState('')
  const configTimeoutRef = useRef(null) // Track timeout for config fetch

  // Gateway info from alive messages
  const [gatewayLiveInfo, setGatewayLiveInfo] = useState(null)
  const [platformMqttConfig, setPlatformMqttConfig] = useState(null)

  const selectedGatewayFamily = useMemo(
    () => (selectedGateway ? getGatewayProductFamily(selectedGateway) : null),
    [selectedGateway]
  )
  const gatewayCaps = useMemo(
    () => getGatewayAdminCapabilities(selectedGateway),
    [selectedGateway]
  )
  const commandOptionsForGateway = useMemo(
    () => COMMAND_OPTIONS.filter((opt) => gatewayCaps.commandIds.includes(opt.id)),
    [gatewayCaps.commandIds]
  )

  useEffect(() => {
    checkUser()
    loadGateways()
    fetchPlatformConfig('mqtt').then(cfg => {
      if (cfg) setPlatformMqttConfig(cfg)
    })
  }, [])

  useEffect(() => {
    if (initialGateway) {
      setSelectedGateway(initialGateway)
    }
  }, [initialGateway?.id])

  useEffect(() => {
    if (configOnly) return undefined
    // Auto-refresh gateway statuses every 30 seconds
    if (autoRefresh && gateways.length > 0) {
      checkAllGatewayStatuses()
      const interval = setInterval(() => {
        checkAllGatewayStatuses()
      }, 30000) // 30 seconds
      setRefreshInterval(interval)
      return () => clearInterval(interval)
    } else if (refreshInterval) {
      clearInterval(refreshInterval)
      setRefreshInterval(null)
    }
  }, [autoRefresh, gateways.length, configOnly])

  // Gateway settings page: auto-connect MQTT admin path (no SSE stream)
  useEffect(() => {
    if (!configOnly || !lockGateway || !selectedGateway || connected || loadingGateways) return
    connectMQTT(selectedGateway)
  }, [configOnly, lockGateway, selectedGateway?.id, connected, loadingGateways])

  useEffect(() => {
    return () => {
      // Cleanup MQTT connection on unmount
      if (mqttClient) {
        if (mqttClient.close) {
          mqttClient.close() // EventSource
        } else if (mqttClient.end) {
          mqttClient.end() // MQTT client (fallback)
        }
      }
    }
  }, [mqttClient])

  // Match dropdown menu width to field width
  useEffect(() => {
    const syncDropdownWidth = () => {
      const commandSelector = document.getElementById('command-selector')
      const modalSelector = document.getElementById('modal-command-selector')
      
      const syncWidth = (selector) => {
        if (!selector) return
        
        const field = selector.closest('.command-dropdown-wrapper')?.querySelector('.cds--list-box__field') ||
                     selector.querySelector('.cds--list-box__field')
        if (!field) return
        
        const fieldWidth = field.offsetWidth
        if (fieldWidth > 0) {
          // Set CSS variable on the wrapper so menu can use it
          const wrapper = selector.closest('.command-dropdown-wrapper')
          if (wrapper) {
            wrapper.style.setProperty('--dropdown-field-width', `${fieldWidth}px`)
          }
          
          // Also directly set on any visible menus
          const menus = document.querySelectorAll('.cds--list-box__menu')
          menus.forEach(menu => {
            menu.style.width = `${fieldWidth}px`
            menu.style.minWidth = `${fieldWidth}px`
            menu.style.maxWidth = `${fieldWidth}px`
          })
        }
      }
      
      syncWidth(commandSelector)
      syncWidth(modalSelector)
    }
    
    // Sync on mount and when dropdown opens
    const observer = new MutationObserver(() => {
      setTimeout(syncDropdownWidth, 50)
    })
    observer.observe(document.body, { childList: true, subtree: true })
    
    // Also sync on window resize
    window.addEventListener('resize', syncDropdownWidth)
    
    // Initial sync with delay to ensure DOM is ready
    const timeoutId = setTimeout(syncDropdownWidth, 200)
    
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', syncDropdownWidth)
      clearTimeout(timeoutId)
    }
  }, [connected, showCommandModal])

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    setUser(user)
  }

  const loadGateways = async () => {
    setLoadingGateways(true)
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (!currentUser) {
        setGateways([])
        return
      }

      const { data, error } = await supabase
        .from('gateways')
        .select('*')
        .eq('user_id', currentUser.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) throw error

      const gatewayList = data || []
      setGateways(gatewayList)

      if (initialGatewayId) {
        const match = gatewayList.find((gw) => gw.id === initialGatewayId)
        if (match) {
          setSelectedGateway(match)
          if (configOnly) {
            fetchGatewayConfig(match.id)
              .then((dbConfig) => {
                if (dbConfig?.success && dbConfig.config) {
                  setCurrentGatewayConfig(dbConfig.config)
                }
              })
              .catch(() => {})
          }
        }
      }

      if (configOnly) {
        const statusMap = {}
        gatewayList.forEach((gw) => {
          const lastSeen = gw.last_seen_at || gw.last_seen
          let isOnline = false
          if (lastSeen) {
            const minutesAgo = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 60000)
            isOnline = minutesAgo <= 15
          }
          statusMap[gw.id] = {
            is_online: isOnline,
            last_seen: lastSeen,
            broker: gw.mqtt_broker_host,
            port: gw.mqtt_broker_port || 1883,
          }
        })
        setGatewayStatuses(statusMap)
      } else if (gatewayList.length > 0) {
        checkAllGatewayStatuses()
      }
    } catch (error) {
      console.error('Error loading gateways:', error)
      toast({
        title: 'Error',
        description: 'Failed to load gateways',
        variant: 'destructive',
      })
    } finally {
      setLoadingGateways(false)
    }
  }

  const checkAllGatewayStatuses = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    
    setLoadingStatuses(true)
    try {
      // Get gateway list + broker info from DB first
      const infoRes = await fetch(`/api/gateways/status?userId=${user.id}`, {
        headers: { 'Content-Type': 'application/json' }
      })
      const infoResult = await infoRes.json()

      if (!infoResult.success) return

      // Seed the status map with broker info from DB
      const statusMap = {}
      infoResult.gateways.forEach(gw => {
        statusMap[gw.gateway_id] = {
          is_online: false,
          last_seen: gw.last_seen,
          broker: gw.broker,
          port: gw.port
        }
      })
      setGatewayStatuses(statusMap)

      // Live MQTT probe for actual online status
      const gatewayIds = infoResult.gateways.map(gw => gw.gateway_id)
      if (gatewayIds.length === 0) return

      const probeRes = await fetch('/api/gateways/probe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gateway_ids: gatewayIds })
      })
      const probeResult = await probeRes.json()

      if (probeResult.success && probeResult.statuses) {
        setGatewayStatuses(prev => {
          const updated = { ...prev }
          Object.entries(probeResult.statuses).forEach(([gwId, status]) => {
            updated[gwId] = {
              ...updated[gwId],
              is_online: status.is_online,
              last_seen: status.last_seen || updated[gwId]?.last_seen,
              probe_error: status.error
            }
          })
          return updated
        })
      }
    } catch (error) {
      console.error('Error checking gateway statuses:', error)
    } finally {
      setLoadingStatuses(false)
    }
  }

  const testGatewayConnection = async (gateway) => {
    setGatewayStatuses(prev => ({
      ...prev,
      [gateway.id]: { ...prev[gateway.id], testing: true }
    }))

    try {
      const result = await probeGateway(gateway)
      const isOnline = !!result.success && !!result.response

      setGatewayStatuses(prev => ({
        ...prev,
        [gateway.id]: {
          ...prev[gateway.id],
          is_online: isOnline,
          last_seen: isOnline ? new Date().toISOString() : prev[gateway.id]?.last_seen,
          testing: false
        }
      }))

      if (isOnline) {
        if (gateway.id) {
          updateGatewayLastSeen(gateway.id).catch(() => {})
        }
        toast({
          title: 'Gateway Online',
          description: `${gateway.name} responded via MQTT`,
        })
      } else {
        toast({
          title: 'Gateway Offline',
          description: result.error || `${gateway.name} did not respond`,
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Error testing gateway connection:', error)
      setGatewayStatuses(prev => ({
        ...prev,
        [gateway.id]: { ...prev[gateway.id], testing: false, is_online: false }
      }))
      toast({
        title: 'Test Failed',
        description: error.message || 'Failed to test gateway connection',
        variant: 'destructive'
      })
    }
  }

  const connectMQTT = async (gateway) => {
    try {
      // Close existing connection if any
      if (mqttClient) {
        if (mqttClient.close) {
          mqttClient.close() // SSE EventSource
        }
        setMqttClient(null)
      }

      // Get MQTT broker info from gateway record, fall back to platform config
      const broker = gateway.mqtt_broker_host || platformMqttConfig?.broker_host
      const port = gateway.mqtt_broker_port || platformMqttConfig?.broker_port || 1883
      const username = gateway.tbmq_username || gateway.mqtt_username
      const password = gateway.tbmq_password || gateway.mqtt_password

      if (!broker || !username || !password) {
        toast({
          title: 'Error',
          description: `Gateway MQTT ${!broker ? 'broker host' : 'credentials'} not found. Ensure the gateway is fully provisioned.`,
          variant: 'destructive'
        })
        return
      }

      // Resolve MQTT topics from gateway row (kbeacon/* vs lilygo/*)
      const mqttTopics = resolveGatewayMqttTopics(gateway)
      const macHex = mqttTopics.macHex
      const publishTopic = mqttTopics.publishTopic
      const subAdminTopic = mqttTopics.subAdminTopic
      const pubAdminTopic = mqttTopics.pubAdminTopic

      // Store connection info for publishing (includes actual admin topics)
      setMqttConnectionInfo({ broker, port, username, password, subAdminTopic, pubAdminTopic, publishTopic })
      setSelectedGateway(gateway)

      if (configOnly) {
        setConnected(true)
        toast({
          title: 'Ready',
          description: `MQTT ready for ${gateway.name}. Loading configuration…`,
        })
        void fetchCurrentConfig(gateway)
        return
      }

      console.log(`🔌 Connecting to MQTT broker via proxy: ${broker}:${port}`)
      console.log(`   Subscribe to: ${pubAdminTopic} (admin responses)`)
      console.log(`   Subscribe to: ${publishTopic} (alive messages & device data)`)
      console.log(`   Publish to: ${subAdminTopic} (admin commands)`)

      // Build topic list for SSE subscription — deduplicate if admin topic equals publish topic
      const topicSet = new Set([pubAdminTopic, publishTopic])
      const params = new URLSearchParams({
        broker,
        port: port.toString(),
        username,
        password,
        topics: Array.from(topicSet).join(',')
      })

      // Use relative path for localhost to avoid CORS issues
      const mqttBridgeUrl = getMqttBridgeUrl()
      const eventSource = new EventSource(`${mqttBridgeUrl}?${params.toString()}`)
      setMqttClient(eventSource) // Store EventSource instead of MQTT client

      eventSource.onopen = () => {
        console.log('✅ SSE connection opened')
        console.log(`📡 Listening for messages on: ${pubAdminTopic} and ${publishTopic}`)
        
        // Automatically fetch gateway configuration after connecting
        setTimeout(() => {
          console.log('🔍 Auto-fetching gateway configuration after connection...')
          toast({
            title: 'Connected to Gateway',
            description: 'Fetching current configuration...',
          })
          fetchCurrentConfig()
        }, 2000) // Wait 2 seconds for connection to stabilize
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📨 Received from proxy:', data)
          console.log('📨 Event data type:', data.type)

          if (data.type === 'connected') {
            setConnected(true)
            console.log(`✅ Connected to MQTT broker for gateway ${gateway.name}`)
          } else if (data.type === 'subscribed') {
            console.log(`✅ Subscribed to ${data.topic}`)
            if (data.topic === pubAdminTopic) {
              console.log(`✅ Successfully subscribed to PubAdmin topic - ready to receive responses!`)
            } else if (data.topic === publishTopic) {
              console.log(`✅ Successfully subscribed to Publish topic - monitoring alive messages & device data!`)
            }
          } else if (data.type === 'message') {
            // Handle incoming MQTT message
            console.log('📨 Raw message received:', {
              topic: data.topic,
              payload: data.payload,
              timestamp: data.timestamp
            })
            
            try {
              const messageData = JSON.parse(data.payload)
              const receivedAt = data.timestamp || new Date().toISOString()
              
              // Check if this is an alive message (gateway heartbeat)
              if (data.topic === publishTopic && messageData.msg === 'alive') {
                console.log('💓 Gateway alive/heartbeat message received')
                console.log('📊 Gateway status:', {
                  firmware: messageData.ver,
                  uptime: messageData.uptime,
                  load: messageData.load,
                  temp: messageData.temp,
                  voltage: messageData.voltage,
                  state: messageData.state
                })
                
                // Update gateway live info
                setGatewayLiveInfo({
                  firmware: messageData.ver,
                  uptime: messageData.uptime,
                  load: messageData.load,
                  temp: messageData.temp,
                  voltage: messageData.voltage,
                  state: messageData.state,
                  mac: messageData.gmac,
                  timestamp: receivedAt
                })
                
                // Update gateway status to online
                if (selectedGateway) {
                  setGatewayStatuses(prev => ({
                    ...prev,
                    [selectedGateway.id]: {
                      ...prev[selectedGateway.id],
                      is_online: true,
                      last_seen: receivedAt,
                      last_heartbeat: receivedAt
                    }
                  }))
                  // Persist last_seen_at to DB so probe fallback works
                  fetch(`/api/gateways/status`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ gatewayId: selectedGateway.id, status: { is_online: true, last_seen: receivedAt } })
                  }).catch(() => {})
                }
              } 
              // Check if this is device data (advData message)
              else if (data.topic === publishTopic && messageData.msg === 'advData') {
                console.log('📡 Gateway device data message received')
                console.log('📊 Device count:', messageData.obj ? messageData.obj.length : 0)
                
                // Update gateway status to online with device data
                if (selectedGateway) {
                  setGatewayStatuses(prev => ({
                    ...prev,
                    [selectedGateway.id]: {
                      ...prev[selectedGateway.id],
                      is_online: true,
                      last_seen: receivedAt,
                      last_device_data: receivedAt,
                      device_count: messageData.obj ? messageData.obj.length : 0
                    }
                  }))
                  // Persist last_seen_at to DB
                  fetch(`/api/gateways/status`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ gatewayId: selectedGateway.id, status: { is_online: true, last_seen: receivedAt } })
                  }).catch(() => {})
                }
              }
              // Handle admin responses (check by message content, not just topic,
              // because gateway may send adminRsp on publish topic instead of pubadmin)
              else if (data.topic === pubAdminTopic || messageData.msg === 'adminRsp' || messageData.msg === 'adminrsp') {
                console.log('📨 Admin response received:', messageData)
                console.log(`📋 Message type: ${messageData.type || 'unknown'}`)
                console.log(`📋 Result: ${messageData.rslt || messageData.result || 'unknown'}`)
                
                // Handle getCfg response (configuration retrieval)
                if (messageData.stype === 'getCfg' && (messageData.rslt === 0 || messageData.result === 0)) {
                  console.log('📋 Gateway configuration received successfully!')
                  console.log('📊 Full configuration from gateway:', messageData)
                  
                  // Log specific credential fields to check if they're present
                  console.log('🔑 Checking for credential fields:')
                  console.log('  MQTT username:', messageData.username || messageData.mqttUser || 'NOT FOUND')
                  console.log('  MQTT password:', messageData.password || messageData.mqttPwd ? '***PRESENT***' : 'NOT FOUND')
                  console.log('  HTTP URL:', messageData.httpUrl || messageData.httpurl || 'NOT FOUND')
                  console.log('  HTTP headers:', messageData.httpHeader || messageData.httpheader || 'NOT FOUND')
                  console.log('  WiFi password:', messageData.wifiPassword || messageData.wifipwd ? '***PRESENT***' : 'NOT FOUND')
                  
                  // Check for all possible credential field names
                  const credentialFields = [
                    'username', 'password', 'mqttUser', 'mqttPwd', 'murl', 'mport',
                    'httpUrl', 'httpurl', 'httpHeader', 'httpheader',
                    'ssid', 'wifiPassword', 'wifipwd', 'encryption'
                  ]
                  
                  console.log('🔍 All possible credential fields:')
                  credentialFields.forEach(field => {
                    const value = messageData[field]
                    if (value !== undefined) {
                      const isPassword = field.toLowerCase().includes('pwd') || field.toLowerCase().includes('password')
                      console.log(`  ${field}:`, isPassword ? '***PRESENT***' : value)
                    } else {
                      console.log(`  ${field}: NOT FOUND`)
                    }
                  })
                  
                  // Store current gateway configuration (this is the REAL config from gateway)
                  setCurrentGatewayConfig(messageData)
                  
                  // Always update the modal fields when we get a getCfg response
                  const formattedConfig = JSON.stringify(messageData, null, 2)
                  setCommandJsonPayload(formattedConfig)
                  setCommandConfig({ config: formattedConfig })

                  // Persist full config to DB so we have correct topics on next connect
                  if (selectedGateway?.id) {
                    saveGatewayConfig(selectedGateway.id, messageData)
                      .catch(err => console.error('Failed to save getCfg config:', err))
                  }
                  
                  // Always clear loading state when we get a successful response
                  setLoadingConfig(false)
                  
                  // Clear any pending timeout
                  if (configTimeoutRef.current) {
                    clearTimeout(configTimeoutRef.current)
                    configTimeoutRef.current = null
                  }
                  
                  // Show appropriate success message
                  if (loadingConfig) {
                    console.log('✅ Configuration formatted and displayed from gateway response')
                    console.log('📋 This is REAL configuration data from the gateway, not demo data!')
                    
                    // Check if credentials are missing and warn user
                    const hasCredentials = messageData.password || messageData.mqttPwd || 
                                        messageData.httpHeader || messageData.wifiPassword ||
                                        messageData.username || messageData.mqttUser
                    
                    if (!hasCredentials) {
                      console.warn('⚠️  No credentials found in getCfg response')
                      console.warn('⚠️  Gateway may not return credentials for security reasons')
                      console.warn('💡 You may need to configure credentials manually')
                      
                      toast({
                        title: '⚠️ Configuration Retrieved (No Credentials)',
                        description: `Gateway configuration received, but credentials may be hidden for security. Check console for details.`,
                        variant: 'default'
                      })
                    } else {
                      toast({
                        title: '✅ Configuration Retrieved from Gateway',
                        description: `Full configuration with credentials received from gateway ${messageData.gmac || 'device'}`,
                      })
                    }
                  } else {
                    console.log('📋 Configuration updated in modal (not in loading state)')
                    // Show success toast for other getCfg responses
                    toast({
                      title: 'Configuration Retrieved',
                      description: `Gateway ${messageData.gmac} configuration loaded successfully`,
                      variant: 'default'
                    })
                  }
                }
                
                setAdminMessages(prev => [{
                  ...messageData,
                  receivedAt: receivedAt,
                  topic: data.topic,
                  source: 'admin_response'
                }, ...prev].slice(0, 50)) // Keep last 50 messages
              }
              
              // Update gateway status to online for any message
              if (selectedGateway && (data.topic === pubAdminTopic || data.topic === publishTopic)) {
                setGatewayStatuses(prev => ({
                  ...prev,
                  [selectedGateway.id]: {
                    ...prev[selectedGateway.id],
                    is_online: true,
                    last_seen: receivedAt
                  }
                }))
              }

              // Show toast for important messages
              if (messageData.type === 'critical_error' || messageData.status === 'error') {
                toast({
                  title: 'Gateway Alert',
                  description: messageData.message || 'Gateway reported an error',
                  variant: 'destructive'
                })
              } else if (messageData.status === 'success') {
                toast({
                  title: 'Command Success',
                  description: messageData.message || 'Command executed successfully'
                })
              } else if (messageData.type === 'system_status' || messageData.command === 'get_status') {
                // Show toast for get_status responses
                toast({
                  title: 'Status Received',
                  description: 'Gateway status information received',
                })
              } else if (messageData.msg === 'adminrsp') {
                // Gateway admin response acknowledgment
                const resultCode = messageData.rslt !== undefined ? messageData.rslt : (messageData.result !== undefined ? messageData.result : -1)
                const isSuccess = resultCode === 0
                const seq = messageData.seq
                
                console.log('✅ Gateway admin response received:', {
                  msg: messageData.msg,
                  stype: messageData.stype,
                  result: resultCode,
                  success: isSuccess,
                  sequence: seq,
                  fullMessage: messageData
                })
                
                // Show toast for admin responses
                if (messageData.stype === 'cfg') {
                  if (isSuccess) {
                    console.log('📢 Showing toast: Configuration Acknowledged')
                    toast({
                      title: 'Configuration Acknowledged',
                      description: `Gateway acknowledged config request (seq: ${seq}). Full config may follow.`,
                    })
                  } else {
                    console.log('📢 Showing toast: Configuration Error')
                    toast({
                      title: 'Configuration Error',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'reboot') {
                  if (isSuccess) {
                    toast({
                      title: 'Reboot Initiated',
                      description: `Gateway will reboot in ${messageData.delay || 5} seconds`,
                    })
                  } else {
                    toast({
                      title: 'Reboot Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'factory') {
                  if (isSuccess) {
                    toast({
                      title: 'Factory Reset Initiated',
                      description: 'Gateway is performing factory reset. This may take several minutes.',
                      variant: 'destructive'
                    })
                  } else {
                    toast({
                      title: 'Factory Reset Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'password') {
                  if (isSuccess) {
                    toast({
                      title: 'Password Changed',
                      description: 'Gateway admin password has been updated successfully.',
                    })
                  } else {
                    toast({
                      title: 'Password Change Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'network') {
                  if (isSuccess) {
                    toast({
                      title: 'Network Configuration Updated',
                      description: 'Gateway network settings have been updated. Gateway may reboot to apply changes.',
                    })
                  } else {
                    toast({
                      title: 'Network Configuration Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'http') {
                  if (isSuccess) {
                    toast({
                      title: 'HTTP Configuration Updated',
                      description: 'Gateway HTTP settings have been updated.',
                    })
                  } else {
                    toast({
                      title: 'HTTP Configuration Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'ap') {
                  if (isSuccess) {
                    toast({
                      title: 'Access Point Configuration Updated',
                      description: 'Gateway AP settings have been updated.',
                    })
                  } else {
                    toast({
                      title: 'Access Point Configuration Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else if (messageData.stype === 'firmware') {
                  if (isSuccess) {
                    toast({
                      title: 'Firmware Update Initiated',
                      description: 'Gateway is downloading and installing firmware update. Do not power off.',
                    })
                  } else {
                    toast({
                      title: 'Firmware Update Failed',
                      description: `Gateway returned error code: ${resultCode}`,
                      variant: 'destructive'
                    })
                  }
                } else {
                  // Other admin response types
                  console.log('📢 Showing toast: Admin Response')
                  toast({
                    title: 'Admin Response',
                    description: `Gateway responded (seq: ${seq}, result: ${isSuccess ? 'Success' : `Error ${resultCode}`})`,
                  })
                }
              } else if (messageData.msg === 'alive') {
                // Gateway heartbeat/alive message - extract configuration info
                console.log('💓 Gateway alive heartbeat received')

                // Extract gateway configuration from alive message
                const gatewayInfo = {
                  version: messageData.ver,
                  mac: messageData.gmac,
                  firmware: messageData.ver ? `v${(messageData.ver / 100).toFixed(2)}` : 'unknown',
                  topics: {
                    pubAdmin: messageData.pubadmin,
                    subAdmin: messageData.subadmin,
                    pubAction: messageData.pubaction,
                    subAction: messageData.subaction
                  },
                  // Additional fields that may be present
                  uploadInterval: messageData.uploadInt,
                  rssiFilter: messageData.fltRssi,
                  macFilter: messageData.fltMac,
                  nameFilter: messageData.fltName,
                  duplicateFilter: messageData.fltDup,
                  // Beacon type flags
                  uploadIBeacon: messageData.uploadIBeacon,
                  uploadEddystone: messageData.uploadEddystone,
                  uploadKSensor: messageData.uploadKSensor
                }

                console.log('📊 Gateway configuration from alive message:', gatewayInfo)

                // Update live gateway info state for UI display
                setGatewayLiveInfo(gatewayInfo)

                // Populate command modal with alive data if no getCfg response yet
                if (!currentGatewayConfig) {
                  const aliveAsConfig = { ...messageData, _source: 'alive_message' }
                  setCurrentGatewayConfig(aliveAsConfig)
                  const formatted = JSON.stringify(aliveAsConfig, null, 2)
                  setCommandJsonPayload(formatted)
                  setCommandConfig({ config: formatted })
                }

                // Save alive message config to database if it contains settings
                const configFromAlive = {}
                if (messageData.uploadInt !== undefined) configFromAlive.uploadInt = messageData.uploadInt
                if (messageData.fltRssi !== undefined) configFromAlive.fltRssi = messageData.fltRssi
                if (messageData.fltMac !== undefined) configFromAlive.fltMac = messageData.fltMac
                if (messageData.fltName !== undefined) configFromAlive.fltName = messageData.fltName
                if (messageData.fltDup !== undefined) configFromAlive.fltDup = messageData.fltDup
                if (messageData.uploadIBeacon !== undefined) configFromAlive.uploadIBeacon = messageData.uploadIBeacon
                if (messageData.uploadEddystone !== undefined) configFromAlive.uploadEddystone = messageData.uploadEddystone
                if (messageData.uploadKSensor !== undefined) configFromAlive.uploadKSensor = messageData.uploadKSensor

                if (Object.keys(configFromAlive).length > 0 && selectedGateway?.id) {
                  console.log('💾 Saving configuration from alive message:', configFromAlive)
                  saveGatewayConfig(selectedGateway.id, configFromAlive)
                    .then(() => {
                      console.log('✅ Configuration from alive message saved to database')
                    })
                    .catch(err => console.error('Failed to save alive config:', err))
                }

                // Discover the gateway's actual admin topics from alive message
                // Field names vary: alive may use subadmin/pubadmin or subadmintopic/pubadmintopic
                const actualSubAdmin = messageData.subadmintopic || messageData.subadmin
                const actualPubAdmin = messageData.pubadmintopic || messageData.pubadmin

                if (actualSubAdmin || actualPubAdmin) {
                  console.log(`📋 Gateway admin topics from alive: sub=${actualSubAdmin}, pub=${actualPubAdmin}`)
                  // Update mqttConnectionInfo so commands go to the right topics
                  setMqttConnectionInfo(prev => {
                    if (!prev) return prev
                    const updated = { ...prev }
                    if (actualSubAdmin) updated.subAdminTopic = actualSubAdmin
                    if (actualPubAdmin) updated.pubAdminTopic = actualPubAdmin
                    console.log(`🔄 Updated admin topics — publish to: ${updated.subAdminTopic}, listen on: ${updated.pubAdminTopic}`)
                    return updated
                  })
                }

                const macHex = messageData.gmac || selectedGateway?.gateway_mac_hex || selectedGateway?.mac_address?.replace(/:/g, '').toUpperCase()
                const expectedSubAdmin = selectedGateway
                  ? resolveGatewayMqttTopics(selectedGateway).subAdminTopic
                  : `kbeacon/subadmin/${macHex}`

                if (!actualSubAdmin) {
                  console.warn('⚠️  Gateway alive message does not include admin topic fields')
                } else if (actualSubAdmin !== expectedSubAdmin) {
                  console.warn(`⚠️  Gateway admin topics differ from convention:`)
                  console.warn(`   Actual subadmin: ${actualSubAdmin}`)
                  console.warn(`   Expected:        ${expectedSubAdmin}`)
                  console.warn(`   Commands will use the gateway's actual topic.`)
                } else {
                  console.log('✅ Gateway has admin topics configured:', {
                    subAdmin: messageData.subadmin,
                    pubAdmin: messageData.pubadmin
                  })
                }
              }
            } catch (parseError) {
              // If not JSON, treat as plain text
              console.log('📨 Received message (plain text):', data.payload)
              console.log('⚠️  Message is not JSON, treating as plain text')
              console.log('⚠️  Parse error:', parseError.message)
              setAdminMessages(prev => [{
                message: data.payload,
                receivedAt: data.timestamp || new Date().toISOString(),
                topic: data.topic,
                raw: true
              }, ...prev].slice(0, 50))
            }
          } else if (data.type === 'error') {
            console.error('Proxy error:', data.message)
            toast({
              title: 'MQTT Error',
              description: data.message,
              variant: 'destructive'
            })
            setConnected(false)
          } else if (data.type === 'disconnected' || data.type === 'offline') {
            console.log('MQTT Disconnected')
            setConnected(false)
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error)
        }
      }

      eventSource.onerror = (error) => {
        console.error('❌ SSE Error:', error)
        console.error('❌ EventSource error details:', {
          readyState: eventSource.readyState,
          url: eventSource.url
        })
        toast({
          title: 'Connection Error',
          description: 'Failed to connect to MQTT proxy. Please try again.',
          variant: 'destructive'
        })
        setConnected(false)
        eventSource.close()
      }

      setSelectedGateway(gateway)
    } catch (error) {
      console.error('Error connecting to MQTT:', error)
      toast({
        title: 'Connection Error',
        description: error.message,
        variant: 'destructive'
      })
    }
  }

  const disconnectMQTT = () => {
    if (mqttClient) {
      if (mqttClient.close) {
        mqttClient.close() // EventSource
      } else if (mqttClient.end) {
        mqttClient.end() // MQTT client (fallback)
      }
      setMqttClient(null)
      setMqttConnectionInfo(null)
      setConnected(false)
      setSelectedGateway(null)
      setAdminMessages([])
      toast({
        title: 'Disconnected',
        description: 'Disconnected from MQTT broker'
      })
    }
  }

  const sendAdminCommand = async (command, payload = {}) => {
    if (!connected || !selectedGateway || !mqttConnectionInfo) {
      toast({
        title: 'Error',
        description: 'Not connected to MQTT broker',
        variant: 'destructive'
      })
      return
    }

    setSendingCommand(true)
    try {
      // Gateway API format according to documentation:
      // Required: msg="admin", stype="cfg", seq=sequence number
      // For get_status, send empty config request to get current configuration
      let commandData

      if (command === 'config') {
        // For config commands from the modal, payload is the complete command
        commandData = payload
        console.log('📤 Sending configuration command (custom JSON):', commandData)
      } else if (command === 'get_status') {
        commandData = buildGetCfgCommand(commandSequence)
        console.log('📋 Requesting gateway configuration parameters including credentials')
        console.log('📤 Using enhanced getCfg format:', commandData)
      } else if (command === 'direct') {
        // For direct commands (reboot, factory_reset, etc.), payload is the complete command
        commandData = payload
        console.log('📤 Sending direct command:', commandData)
      } else {
        // For configuration commands, use gateway API format
        commandData = {
          msg: 'admin',
          stype: 'cfg', // Configuration command
          seq: commandSequence,
          user: 'admin',     // Add authentication username
          pwd: 'admin',     // Add authentication password
          ...payload // Include command-specific parameters
        }
        console.log('📤 Sending configuration command:', command)
      }
      
      // Increment sequence for next command
      setCommandSequence(prev => prev + 1)

      // Send via Netlify MQTT admin — lilygo/* or kbeacon/* topics resolved from gateway row
      const result = await sendMqttAdminCommand(selectedGateway, {
        command: commandData,
        timeout: 30,
        waitForResponse: true,
      })

      if (!result.success) {
        console.error('Admin command error (bridge):', result.error)
        toast({
          title: 'Error',
          description: result.error || (result.timeout ? 'Gateway did not respond in time' : 'Failed to send command'),
          variant: 'destructive'
        })
        setSendingCommand(false)
      } else {
        console.log('✅ Admin command sent via bridge:', commandData)
        console.log('✅ Gateway response received:', result.response)
        
        // Add gateway response to admin messages for display
        if (result.response) {
          const adminTopics = resolveGatewayMqttTopics(selectedGateway)
          setAdminMessages(prev => [{
            ...result.response,
            receivedAt: new Date().toISOString(),
            topic: adminTopics.pubAdminTopic,
            source: 'bridge_response'
          }, ...prev].slice(0, 50))
        }
        
        // Add to command history with response
        setCommandHistory(prev => [{
          command,
          payload: commandData,
          sentAt: new Date().toISOString(),
          response: result.response,
          responseReceived: !!result.response
        }, ...prev].slice(0, 20))

        // Show success toast with response details
        if (result.response) {
          const responseMsg = result.response.msg || 'adminrsp'
          const responseResult = result.response.rslt || result.response.result || 'unknown'
          const isSuccess = responseResult === 'succ' || responseResult === 0

          toast({
            title: isSuccess ? 'Command Successful' : 'Command Response',
            description: result.response.message ||
              (isSuccess ? 'Gateway executed command successfully' : `Gateway response: ${responseResult}`),
            variant: isSuccess ? 'default' : 'destructive'
          })

          // Update gateway last_seen timestamp since it responded
          if (selectedGateway?.id) {
            updateGatewayLastSeen(selectedGateway.id).catch(err => console.error('Failed to update last_seen:', err))

            // If config command was successful, save it to database for later retrieval
            if (isSuccess && (commandData.msg === 'admin' || command === 'config')) {
              const configToSave = { ...commandData }
              // Remove msg/stype/seq from saved config (keep only actual settings)
              delete configToSave.msg
              delete configToSave.stype
              delete configToSave.seq

              // Only save if there are actual config parameters
              if (Object.keys(configToSave).length > 0) {
                saveGatewayConfig(selectedGateway.id, configToSave)
                  .then(() => {
                    console.log('✅ Configuration saved to database:', configToSave)
                  })
                  .catch(err => console.error('Failed to save config to database:', err))
              }
            }
          }
        } else {
        toast({
          title: 'Command Sent',
            description: 'Command sent to gateway. Waiting for response...'
        })
        }

        setSendingCommand(false)
      }
    } catch (error) {
      console.error('Error sending command:', error)
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      })
      setSendingCommand(false)
    }
  }

  const fetchCurrentConfig = async (gatewayOverride) => {
    const gateway = gatewayOverride || selectedGateway
    if (!gateway) {
      toast({
        title: 'Error',
        description: 'No gateway selected',
        variant: 'destructive'
      })
      return
    }

    if (!connected && !configOnly) {
      toast({
        title: 'Error',
        description: 'Not connected to gateway. Please connect first.',
        variant: 'destructive'
      })
      return
    }

    setLoadingConfig(true)
    try {
      // Send enhanced getCfg command to request full configuration including credentials from gateway
      console.log('📋 Requesting full configuration including credentials from gateway via MQTT (getCfg)...')

      const currentSeq = commandSequence
      const commandData = buildGetCfgCommand(currentSeq)

      setCommandSequence(prev => prev + 1)

      console.log('📤 Sending getCfg command to gateway:', commandData)

      const result = await sendMqttAdminCommand(gateway, {
        command: commandData,
        timeout: 30,
        waitForResponse: true,
      })

      if (!result.success) {
        console.error('❌ Failed to send getCfg command:', result.error)
        const isTimeout = result.timeout || /did not respond/i.test(result.error || '')
        toast({
          title: isTimeout ? 'Gateway Not Responding' : 'Configuration Error',
          description: result.error || 'Failed to load configuration from gateway',
          variant: 'destructive'
        })

        // Fallback: Load from database
        console.log('📋 Loading last known configuration from database as fallback...')
        const dbConfig = await fetchGatewayConfig(gateway.id)
        if (dbConfig.success && dbConfig.config) {
          console.log('✅ Loaded last known config from database:', dbConfig.config)
          const formattedConfig = JSON.stringify(dbConfig.config, null, 2)
          setCommandJsonPayload(formattedConfig)
          setCommandConfig({ config: formattedConfig })
          setCurrentGatewayConfig(dbConfig.config)

          toast({
            title: 'Configuration Loaded',
            description: isTimeout
              ? 'Gateway did not respond (offline or unreachable). Showing last saved configuration.'
              : 'Showing last saved configuration from database.',
          })
        }
        setLoadingConfig(false)
        return
      }

      if (result.response) {
        setCurrentGatewayConfig(result.response)
        const formattedConfig = JSON.stringify(result.response, null, 2)
        setCommandJsonPayload(formattedConfig)
        setCommandConfig({ config: formattedConfig })
        setLoadingConfig(false)
        if (configTimeoutRef.current) {
          clearTimeout(configTimeoutRef.current)
          configTimeoutRef.current = null
        }
        toast({
          title: 'Configuration Loaded',
          description: 'Received current configuration from gateway.',
        })
        return
      }

      if (configOnly) {
        setLoadingConfig(false)
        return
      }

      // Full admin view: wait for SSE if bridge returned before response body
      // The SSE handler (eventSource.onmessage) will receive the getCfg response
      // and automatically update currentGatewayConfig and display it (see SSE handler around line 613)
      console.log('✅ getCfg command sent successfully (seq: ' + currentSeq + ')')
      console.log('📡 Waiting for gateway to send full configuration via PubAdmin topic...')
      console.log('💡 The configuration will appear automatically when received from gateway')
      console.log('⏳ This may take a few seconds...')

      // Set a timeout to handle cases where gateway doesn't respond
      configTimeoutRef.current = setTimeout(() => {
        if (loadingConfig) {
          console.log('⏰ Timeout waiting for gateway config response')
          setLoadingConfig(false)
          
          // Fallback to database
          console.log('📋 Loading last known configuration from database as fallback...')
          fetchGatewayConfig(selectedGateway.id)
            .then(dbConfig => {
              if (dbConfig?.success && dbConfig.config) {
                console.log('✅ Loaded from database:', dbConfig.config)
                const formattedConfig = JSON.stringify(dbConfig.config, null, 2)
                setCommandJsonPayload(formattedConfig)
                setCommandConfig({ config: formattedConfig })
                setCurrentGatewayConfig(dbConfig.config)
                toast({
                  title: 'Configuration Loaded (Database)',
                  description: 'Gateway did not respond. Showing last known configuration from database.',
                })
              } else {
                toast({
                  title: 'Timeout',
                  description: 'Gateway did not respond with configuration. Please try again.',
                  variant: 'destructive'
                })
              }
            })
            .catch(err => {
              console.error('Error loading from database:', err)
              toast({
                title: 'Error',
                description: 'Failed to load configuration',
                variant: 'destructive'
                })
            })
        }
        configTimeoutRef.current = null
      }, 10000) // 10 second timeout

      // The SSE handler will clear this timeout when it receives the config

      // Show immediate feedback
      toast({
        title: 'Requesting Configuration',
        description: 'Waiting for gateway to send full configuration (this is REAL data from gateway)...',
      })

    } catch (error) {
      console.error('Error fetching config:', error)
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      })
      setLoadingConfig(false)
    }
  }

  const handleReboot = () => {
    if (confirm('Are you sure you want to reboot the gateway? It will disconnect temporarily.')) {
      sendAdminCommand('reboot', { delay: 5 })
    }
  }

  const handleFactoryReset = () => {
    if (confirm('⚠️ WARNING: This will erase ALL gateway settings. Are you absolutely sure?')) {
      if (confirm('This action cannot be undone. Type \"RESET\" to confirm:')) {
        sendAdminCommand('factory_reset', { confirm: true })
      }
    }
  }

  const handleChangePassword = () => {
    if (!newPassword || newPassword.length < 5) {
      toast({
        title: 'Error',
        description: 'Password must be at least 5 characters',
        variant: 'destructive'
      })
      return
    }
    sendAdminCommand('change_password', { new_password: newPassword })
    setNewPassword('')
  }

  const handleFirmwareUpdate = () => {
    if (!firmwareUrl || !firmwareVersion) {
      toast({
        title: 'Error',
        description: 'Firmware URL and version are required',
        variant: 'destructive'
      })
      return
    }
    sendAdminCommand('update_firmware', {
      firmware: {
        url: firmwareUrl,
        version: firmwareVersion
      }
    })
    setFirmwareUrl('')
    setFirmwareVersion('')
  }

  const handleGetStatus = async () => {
    // Note: Gateway can only publish to one topic at a time (Alive Message Topic)
    // The gateway's "Alive Message Topic" must be manually set to "Pub Admin Topic"
    // in the gateway's admin interface for admin responses to work.
    // We cannot change this remotely via HTTP API (gateway IP is local network only).
    
    if (!selectedGateway) {
      toast({
        title: 'Error',
        description: 'No gateway selected',
        variant: 'destructive'
      })
      return
    }

    // Since gateway IP is local network only (not accessible over internet),
    // we cannot programmatically change the "Alive Message Topic" setting.
    // The gateway must be manually configured to use "Pub Admin Topic" for admin responses.
    
    console.log('📤 Sending get_status command to retrieve full gateway configuration...')
    console.log('💡 Using getCfg stype to get all configuration parameters')
    console.log('💡 Gateway will respond with complete configuration including:')
    console.log('   - Firmware version, BLE version, upload period')
    console.log('   - RSSI filter, packet size, MQTT configuration')
    console.log('   - Beacon types, scanning settings, GPS configuration')
    console.log('💡 If no response, check gateway admin interface for MQTT configuration')
    
    sendAdminCommand('get_status')
  }

  return (
    <div className={`gateway-admin-view space-y-6 min-w-0 max-w-full overflow-x-hidden${lockGateway ? ' gateway-admin-view--locked' : ''}`}>
      {/* Page Title — hidden on per-gateway settings page (parent already shows header) */}
      {!lockGateway && (
      <Tile className="space-y-1 border border-[#e0e0e0] bg-white">
        <div className="flex items-center gap-2">
          <Settings className="h-6 w-6 text-gray-700" />
          <h2 className="text-lg font-semibold text-[#161616]">
            {configOnly ? 'Gateway Config & OTA' : gatewayCaps.adminTitle}
          </h2>
          {selectedGatewayFamily && (
            <Badge variant="outline">{selectedGatewayFamily}</Badge>
          )}
        </div>
        <p className="text-sm text-[#525252]">
          {configOnly
            ? 'Push configuration changes and OTA commands to your gateways via MQTT'
            : gatewayCaps.settingsStatusHint}
        </p>
      </Tile>
      )}

      {/* Gateway Status — compact on per-gateway settings (native admin screens) */}
      {lockGateway && selectedGateway ? (
        <GatewayCompactConnectBar
          gateway={selectedGateway}
          connected={connected}
          isOnline={gatewayStatuses[selectedGateway.id]?.is_online}
          isTesting={gatewayStatuses[selectedGateway.id]?.testing}
          lastSeen={gatewayStatuses[selectedGateway.id]?.last_seen || selectedGateway.last_seen_at}
          onTest={() => testGatewayConnection(selectedGateway)}
          onToggleConnect={() => {
            if (connected) disconnectMQTT()
            else connectMQTT(selectedGateway)
          }}
        />
      ) : (
      <Tile className="border border-[#e0e0e0] bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-[#161616]">Gateway Status</h3>
            <p className="text-sm text-[#525252]">
          {configOnly
            ? (lockGateway
              ? gatewayCaps.settingsStatusHint
              : 'Select a gateway and connect to send configuration or OTA commands')
            : 'Monitor all your gateways and their connection status'}
            </p>
          </div>
          {!configOnly && (
          <div className="flex items-center gap-2">
            <CarbonButton
              kind="secondary"
              size="sm"
              onClick={checkAllGatewayStatuses}
              disabled={loadingStatuses}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingStatuses ? 'animate-spin' : ''}`} />
              Refresh
            </CarbonButton>
            <CarbonButton
              kind={autoRefresh ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
            </CarbonButton>
          </div>
          )}
        </div>
        <div>
          {loadingGateways ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : gateways.length === 0 ? (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No gateways found. Please assign a gateway first.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {(lockGateway && initialGatewayId
                ? gateways.filter((gw) => gw.id === initialGatewayId)
                : gateways
              ).map((gateway) => {
                const status = gatewayStatuses[gateway.id] || {}
                const isOnline = status.is_online
                const isTesting = status.testing
                const lastSeen = status.last_seen
                
                return (
                  <div
                    key={gateway.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-semibold">{gateway.name}</h3>
                          <GatewayTypeTags gateway={gateway} showSource={false} />
                          {isOnline ? (
                            <Badge variant="default" className="bg-green-500">
                              <div className="w-2 h-2 rounded-full bg-white mr-1.5" />
                              Online
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <div className="w-2 h-2 rounded-full bg-gray-400 mr-1.5" />
                              Offline
                            </Badge>
                          )}
                          {isTesting && (
                            <Badge variant="outline">
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Testing...
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>MAC: <span className="font-mono">{gateway.mac_address}</span></div>
                          {gateway.mqtt_broker_host && (
                            <div>Broker: <span className="font-mono">{gateway.mqtt_broker_host}:{gateway.mqtt_broker_port || 1883}</span></div>
                          )}
                          {lastSeen && (
                            <div>
                              Last Seen: {new Date(lastSeen).toLocaleString()}
                              {' '}
                              ({Math.round((Date.now() - new Date(lastSeen).getTime()) / 1000 / 60)} min ago)
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CarbonButton
                          kind="secondary"
                          size="sm"
                          onClick={() => testGatewayConnection(gateway)}
                          disabled={isTesting}
                        >
                          {isTesting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Activity className="h-4 w-4 mr-2" />
                              Test Connection
                            </>
                          )}
                        </CarbonButton>
                        <CarbonButton
                          kind={selectedGateway?.id === gateway.id && connected ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => {
                            if (selectedGateway?.id === gateway.id && connected) {
                              disconnectMQTT()
                            } else {
                              connectMQTT(gateway)
                            }
                          }}
                        >
                          {selectedGateway?.id === gateway.id && connected ? 'Disconnect' : 'Connect'}
                        </CarbonButton>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Tile>
      )}

      {/* Admin Tabs — tab order matches native GatewayAdmin vs LilyGoGatewayAdmin */}
      {selectedGateway && (
        <Tabs defaultValue={gatewayCaps.defaultSettingsTab} className="space-y-4">
          <TabsList className="inline-flex w-full mb-2 border-b border-[#e0e0e0] bg-transparent rounded-none p-0">
            {gatewayCaps.settingsTabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex-1 text-center px-4 py-2 text-sm font-medium text-[#525252] bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-[#0f62fe] data-[state=active]:text-[#161616]"
              >
                {tab.label}
              </TabsTrigger>
            ))}
            {!configOnly && gatewayCaps.showMessagesTab && (
            <TabsTrigger
              value="messages"
              className="flex-1 text-center px-4 py-2 text-sm font-medium text-[#525252] bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-[#0f62fe] data-[state=active]:text-[#161616]"
            >
              Messages
            </TabsTrigger>
            )}
            {!configOnly && gatewayCaps.showHistoryTab && (
            <TabsTrigger
              value="history"
              className="flex-1 text-center px-4 py-2 text-sm font-medium text-[#525252] bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-[#0f62fe] data-[state=active]:text-[#161616]"
            >
              History
            </TabsTrigger>
            )}
          </TabsList>

          {/* Commands Tab */}
          <TabsContent value="commands" className="space-y-4">
            {/* Command Selector */}
            <Tile className="border border-[#e0e0e0] bg-white p-4">
              {!connected && (
                <Alert className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Connect to the gateway MQTT broker to send commands. Click the "Connect" button above.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold text-[#161616]">
                    {gatewayCaps.isKGateway ? 'Send admin command' : 'Remote commands'}
                  </h3>
                  <p className="text-sm text-[#525252]">
                    {connected
                      ? (gatewayCaps.isKGateway
                        ? 'KBeacon MQTT admin commands (native GatewayAdmin)'
                        : `${gatewayCaps.family} MQTT commands (native LilyGoGatewayAdmin)`)
                      : 'Connect above to enable command sending'}
                  </p>
                </div>
                <CarbonButton
                  kind="primary"
                  className="gateway-admin-action-btn shrink-0"
                  onClick={() => setShowCommandModal(true)}
                  disabled={sendingCommand || !connected}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Command
                </CarbonButton>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Label className="text-sm shrink-0">Command Type:</Label>
                <div className="command-dropdown-wrapper gateway-admin-dropdown min-w-0 flex-1 max-w-full">
                  <Dropdown
                    id="command-selector"
                    title="Select Command"
                    label="Select Command"
                    items={commandOptionsForGateway}
                    itemToString={(item) => (item ? item.text : '')}
                    selectedItem={commandOptionsForGateway.find((opt) => opt.id === selectedCommandType) || null}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        setSelectedCommandType(selectedItem.id)
                        setShowCommandModal(true)
                      }
                    }}
                    size="md"
                    disabled={!connected}
                  />
                </div>
              </div>
            </Tile>

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 min-w-0">
              {/* System Commands */}
              <Tile className="space-y-4 border border-[#e0e0e0] bg-white p-4">
                <div className="text-sm font-semibold text-[#161616]">System Commands</div>
                  <div>
                    <CarbonButton
                      kind="secondary"
                      className="w-full"
                      onClick={handleReboot}
                      disabled={sendingCommand || !connected}
                    >
                      <Power className="h-4 w-4 mr-2" />
                      Reboot Gateway
                    </CarbonButton>
                    <p className="text-xs text-gray-500 mt-1">
                      Restart the gateway (temporary disconnection)
                    </p>
                  </div>

                  <div>
                    <CarbonButton
                      kind="danger"
                      className="w-full"
                      onClick={handleFactoryReset}
                      disabled={sendingCommand || !connected}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Factory Reset
                    </CarbonButton>
                    <p className="text-xs text-gray-500 mt-1">
                      ⚠️ Erases all settings
                    </p>
                  </div>

                  <div>
                    <CarbonButton
                      kind="secondary"
                      className="w-full"
                      onClick={handleGetStatus}
                      disabled={sendingCommand || !connected}
                    >
                      <Activity className="h-4 w-4 mr-2" />
                      Get System Status
                    </CarbonButton>
                    <p className="text-xs text-gray-500 mt-1">
                      Request current gateway status
                    </p>
                  </div>
                </Tile>

              {/* Security Commands */}
              <Tile className="space-y-4 border border-[#e0e0e0] bg-white p-4">
                <div className="text-sm font-semibold text-[#161616]">Security</div>
                  <div>
                    <Label>Change Admin Password</Label>
                    <div className="flex gap-2 mt-2">
                      <TextInput
                        id="adminNewPassword"
                        labelText=""
                        type="password"
                        placeholder="New password (min 5 chars)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        size="sm"
                        disabled={sendingCommand || !connected}
                      />
                      <CarbonButton
                        kind="primary"
                        size="sm"
                        onClick={handleChangePassword}
                        disabled={sendingCommand || !newPassword || !connected}
                      >
                        <Key className="h-4 w-4" />
                      </CarbonButton>
                    </div>
                  </div>
                </Tile>

              {/* Firmware Update */}
              <Tile className="space-y-4 border border-[#e0e0e0] bg-white p-4 min-w-0">
                <div className="text-sm font-semibold text-[#161616]">Firmware Update</div>
                {gatewayCaps.isEsp ? (
                  <EspFirmwareOtaPanel
                    gateway={selectedGateway}
                    disabled={sendingCommand || !connected}
                    onScheduled={(result) => {
                      toast({
                        title: 'OTA Scheduled',
                        description: `Gateway will update to ${result.toVersion} when it polls online.`,
                      })
                    }}
                  />
                ) : (
                  <>
                  <div>
                    <TextInput
                      id="firmwareUrl"
                      labelText="Firmware URL"
                      placeholder="https://example.com/firmware.bin"
                      value={firmwareUrl}
                      onChange={(e) => setFirmwareUrl(e.target.value)}
                      disabled={sendingCommand || !connected}
                      size="sm"
                    />
                  </div>
                  <div>
                    <TextInput
                      id="firmwareVersion"
                      labelText="Version"
                      placeholder="2.1.0"
                      value={firmwareVersion}
                      onChange={(e) => setFirmwareVersion(e.target.value)}
                      disabled={sendingCommand || !connected}
                      size="sm"
                    />
                  </div>
                  <CarbonButton
                    kind="primary"
                    className="gateway-admin-action-btn w-full"
                    onClick={handleFirmwareUpdate}
                    disabled={sendingCommand || !firmwareUrl || !firmwareVersion || !connected}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Update Firmware
                  </CarbonButton>
                  </>
                )}
                </Tile>

              {/* Configuration Commands (KGateway provisioning note) */}
              {gatewayCaps.showProvisioningConfigNote && (
              <Tile className="space-y-4 border border-[#e0e0e0] bg-white p-4">
                <div className="text-sm font-semibold text-[#161616]">BLE &amp; beacon configuration</div>
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      BLE filters, beacon types, and HTTP upload use full config JSON.
                      Use Get Current Config first, then edit and send via Commands.
                    </AlertDescription>
                  </Alert>
                  <div className="space-y-2">
                    <CarbonButton
                      kind="secondary"
                      className="w-full"
                      onClick={handleGetStatus}
                      disabled={sendingCommand || !connected}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Get Current Config
                    </CarbonButton>
                  </div>
                </Tile>
              )}
            </div>

            {sendingCommand && (
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Sending command to gateway...
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* Configuration Tab */}
          <TabsContent value="config">
            <Tile className="space-y-4 border border-[#e0e0e0] bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-[#161616]">{gatewayCaps.configTabTitle}</h3>
                  <p className="text-sm text-[#525252]">
                    {gatewayCaps.configTabSubtitle}
                  </p>
                  {gatewayCaps.networkHint && (
                    <p className="text-xs text-[#0f62fe] mt-1">{gatewayCaps.networkHint}</p>
                  )}
                </div>
                <CarbonButton
                  kind="secondary"
                  size="sm"
                  onClick={() => fetchCurrentConfig()}
                  disabled={loadingConfig || (!connected && !configOnly)}
                >
                  {loadingConfig ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Reload Config
                </CarbonButton>
              </div>

              {/* Live Info from Alive Messages (KGateway only) */}
              {gatewayCaps.usesAliveHeartbeat && gatewayLiveInfo && (
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="text-sm font-semibold text-[#161616] mb-3 flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Live Gateway Information (from heartbeat)
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-[#525252]">Firmware Version:</span>
                      <p className="text-[#161616]">{gatewayLiveInfo.firmware}</p>
                    </div>
                    <div>
                      <span className="font-medium text-[#525252]">MAC Address:</span>
                      <p className="text-[#161616] font-mono">{gatewayLiveInfo.mac}</p>
                    </div>
                    {gatewayLiveInfo.uploadInterval !== undefined && (
                      <div>
                        <span className="font-medium text-[#525252]">Upload Interval:</span>
                        <p className="text-[#161616]">{gatewayLiveInfo.uploadInterval}s ({Math.round(gatewayLiveInfo.uploadInterval / 60)}min)</p>
                      </div>
                    )}
                    {gatewayLiveInfo.rssiFilter !== undefined && (
                      <div>
                        <span className="font-medium text-[#525252]">RSSI Filter:</span>
                        <p className="text-[#161616]">{gatewayLiveInfo.rssiFilter} dBm</p>
                      </div>
                    )}
                    {gatewayLiveInfo.macFilter !== undefined && (
                      <div className="col-span-2">
                        <span className="font-medium text-[#525252]">MAC Filter:</span>
                        <p className="text-[#161616] font-mono text-xs">{gatewayLiveInfo.macFilter || 'None'}</p>
                      </div>
                    )}
                    {gatewayLiveInfo.nameFilter !== undefined && (
                      <div className="col-span-2">
                        <span className="font-medium text-[#525252]">Name Filter:</span>
                        <p className="text-[#161616] font-mono text-xs">{gatewayLiveInfo.nameFilter || 'None'}</p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="font-medium text-[#525252]">Beacon Types Enabled:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {gatewayLiveInfo.uploadIBeacon === 1 && <Badge variant="default">iBeacon</Badge>}
                        {gatewayLiveInfo.uploadEddystone === 1 && <Badge variant="default">Eddystone</Badge>}
                        {gatewayLiveInfo.uploadKSensor === 1 && <Badge variant="default">KSensor</Badge>}
                        {!gatewayLiveInfo.uploadIBeacon && !gatewayLiveInfo.uploadEddystone && !gatewayLiveInfo.uploadKSensor && (
                          <span className="text-sm text-gray-500">No beacon type information available</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {gatewayLiveInfo.topics && (
                    <details className="mt-4">
                      <summary className="cursor-pointer text-sm font-medium text-[#525252] hover:text-[#161616]">
                        MQTT Topics Configuration
                      </summary>
                      <div className="mt-2 space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="font-medium">Pub Admin:</span>
                            <p className="font-mono text-[#161616]">{gatewayLiveInfo.topics.pubAdmin || 'Not set'}</p>
                          </div>
                          <div>
                            <span className="font-medium">Sub Admin:</span>
                            <p className="font-mono text-[#161616]">{gatewayLiveInfo.topics.subAdmin || 'Not set'}</p>
                          </div>
                          <div>
                            <span className="font-medium">Pub Action:</span>
                            <p className="font-mono text-[#161616]">{gatewayLiveInfo.topics.pubAction || 'Not set'}</p>
                          </div>
                          <div>
                            <span className="font-medium">Sub Action:</span>
                            <p className="font-mono text-[#161616]">{gatewayLiveInfo.topics.subAction || 'Not set'}</p>
                          </div>
                        </div>
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* ESP config summary (from getCfg / database) */}
              {gatewayCaps.isEsp && currentGatewayConfig && (
                <div className="p-4 border border-teal-200 rounded-lg bg-teal-50">
                  <h4 className="text-sm font-semibold text-[#161616] mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {gatewayCaps.family} settings (MQTT getCfg)
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {(currentGatewayConfig.fwVersion || currentGatewayConfig.firmware) && (
                      <div>
                        <span className="font-medium text-[#525252]">Firmware:</span>
                        <p className="text-[#161616]">{currentGatewayConfig.fwVersion || currentGatewayConfig.firmware}</p>
                      </div>
                    )}
                    {(currentGatewayConfig.rptPeriod || currentGatewayConfig.uploadInterval) && (
                      <div>
                        <span className="font-medium text-[#525252]">Upload interval:</span>
                        <p className="text-[#161616]">
                          {currentGatewayConfig.rptPeriod || currentGatewayConfig.uploadInterval}s
                        </p>
                      </div>
                    )}
                    {currentGatewayConfig.filterrssi !== undefined && (
                      <div>
                        <span className="font-medium text-[#525252]">RSSI filter:</span>
                        <p className="text-[#161616]">{currentGatewayConfig.filterrssi} dBm</p>
                      </div>
                    )}
                    {currentGatewayConfig.filtermac !== undefined && (
                      <div className="col-span-2">
                        <span className="font-medium text-[#525252]">MAC filter:</span>
                        <p className="text-[#161616] font-mono text-xs">{currentGatewayConfig.filtermac || 'None'}</p>
                      </div>
                    )}
                    {(currentGatewayConfig.murl || currentGatewayConfig.mqtt_broker_host) && (
                      <div className="col-span-2">
                        <span className="font-medium text-[#525252]">MQTT broker:</span>
                        <p className="text-[#161616] font-mono text-xs">
                          {currentGatewayConfig.murl || currentGatewayConfig.mqtt_broker_host}
                          {currentGatewayConfig.mport ? `:${currentGatewayConfig.mport}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Last Known Config from Database / getCfg */}
              {currentGatewayConfig && (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  {gatewayCaps.isEsp ? (
                    <details>
                      <summary className="text-sm font-semibold text-[#161616] mb-3 flex items-center gap-2 cursor-pointer">
                        <Settings className="h-4 w-4 inline" />
                        Full configuration (JSON)
                      </summary>
                      <pre className="text-xs bg-white p-3 rounded border overflow-x-auto mt-3">
                        {JSON.stringify(currentGatewayConfig, null, 2)}
                      </pre>
                      <p className="text-xs text-gray-500 mt-2">
                        Raw JSON from getCfg or the last configuration saved for this gateway.
                      </p>
                    </details>
                  ) : (
                    <>
                      <h4 className="text-sm font-semibold text-[#161616] mb-3 flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Last applied configuration (from database)
                      </h4>
                      <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                        {JSON.stringify(currentGatewayConfig, null, 2)}
                      </pre>
                      <p className="text-xs text-gray-500 mt-2">
                        This is the last configuration you sent to the gateway that was acknowledged.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Empty State */}
              {!gatewayLiveInfo && !currentGatewayConfig && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {loadingConfig ? (
                      <>Loading configuration from gateway via MQTT (getCfg)…</>
                    ) : configOnly ? (
                      !connected ? (
                        <>Connecting to MQTT… The gateway must be online on the broker to respond.</>
                      ) : (
                        <>
                          No configuration loaded yet. Click <strong>Reload Config</strong> to fetch settings via MQTT,
                          or use the Commands tab → Get Current Configuration. If the gateway is offline, only the last
                          saved configuration (if any) can be shown.
                        </>
                      )
                    ) : !connected ? (
                      gatewayCaps.isEsp ? (
                        <>Connect to MQTT, then click <strong>Reload Config</strong> to fetch {gatewayCaps.family} settings.</>
                      ) : (
                        <>Connect to the gateway to view configuration from alive messages.</>
                      )
                    ) : gatewayCaps.isEsp ? (
                      <>Click <strong>Reload Config</strong> to fetch settings via MQTT getCfg. If the gateway is offline, only the last saved copy is shown.</>
                    ) : (
                      <>Waiting for gateway heartbeat message… This may take up to 60 seconds. Use Reload Config to request settings via MQTT.</>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Configuration Guide */}
              <div className="p-4 border border-gray-200 rounded-lg bg-white">
                <h4 className="text-sm font-semibold text-[#161616] mb-2">Configuration Guide</h4>
                <div className="text-sm text-[#525252] space-y-2">
                  {gatewayCaps.isKGateway ? (
                    <>
                      <p>
                        <strong>KGateway:</strong> Full configuration is not always included in periodic{' '}
                        <code className="text-xs bg-gray-100 px-1 rounded">alive</code> messages. Use{' '}
                        <strong>Reload Config</strong> (MQTT <code className="text-xs bg-gray-100 px-1 rounded">getCfg</code>)
                        or the Commands tab to request the current settings.
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Live status fields come from heartbeat / alive messages when connected via the admin view</li>
                        <li>Last saved config is shown when the gateway is offline or does not respond</li>
                        <li>Use the Commands tab to push updated configuration to the gateway</li>
                      </ul>
                    </>
                  ) : gatewayCaps.isLGateway ? (
                    <>
                      <p>
                        <strong>LGateway (LilyGO T-SIM7670G):</strong> Wi‑Fi and LTE provisioning is done via{' '}
                        <strong>Configure network</strong> (SoftAP). Remote admin uses MQTT getCfg for filters,
                        upload timing, broker settings, reboot, and OTA.
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>BLE/beacon filter commands are not used on this hardware</li>
                        <li>Reload Config fetches the live JSON from the device when online</li>
                        <li>Use Commands for OTA firmware updates and MQTT credential changes</li>
                      </ul>
                    </>
                  ) : gatewayCaps.isMGateway ? (
                    <>
                      <p>
                        <strong>MGateway (Wemos Lolin32):</strong> Wi‑Fi provisioning is done via{' '}
                        <strong>Configure network</strong> (SoftAP). Remote admin uses MQTT getCfg for filters,
                        upload timing, broker settings, reboot, and OTA.
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>BLE/beacon filter commands are not used on this hardware</li>
                        <li>Reload Config fetches the live JSON from the device when online</li>
                        <li>Use Commands for OTA firmware updates and MQTT credential changes</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>{selectedGatewayFamily || 'Gateway'}:</strong> Current configuration can be retrieved via MQTT{' '}
                        <code className="text-xs bg-gray-100 px-1 rounded">getCfg</code>. Click{' '}
                        <strong>Reload Config</strong> when the gateway is online.
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Settings are fetched directly from the gateway over the admin MQTT topics</li>
                        <li>If the gateway is offline, the last saved configuration from the database is shown</li>
                        <li>Use the Commands tab to update gateway configuration or run OTA</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </Tile>
          </TabsContent>

          {/* Messages Tab */}
          {!configOnly && (
          <TabsContent value="messages">
            <Tile className="space-y-3 border border-[#e0e0e0] bg-white p-4">
              <div>
                <h3 className="text-base font-semibold text-[#161616]">Gateway Messages</h3>
                <p className="text-sm text-[#525252]">
                  Real-time messages from gateway (PubAdmin topic)
                  {!connected && (
                    <span className="text-red-500 ml-2">⚠️ Not connected - Connect to gateway first</span>
                  )}
                  {connected && (
                    <span className="text-green-500 ml-2">✅ Connected - Waiting for messages...</span>
                  )}
                </p>
              </div>
                {adminMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-2">
                      No messages received yet.
                    </p>
                    {!connected ? (
                      <p className="text-sm text-orange-600">
                        Click "Connect" button above to start receiving messages.
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400">
                        Gateway will send status updates here when you send commands.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {adminMessages.map((msg, idx) => {
                      const isAlive = msg.msg === 'alive'
                      const isAdminRsp = msg.msg === 'adminrsp'
                      const isStatusResponse = msg.type === 'system_status' || msg.command === 'get_status'
                      const isError = msg.status === 'error' || msg.type === 'critical_error' || (isAdminRsp && msg.rslt !== 0)
                      
                      return (
                      <div
                        key={idx}
                          className={`p-3 border rounded-lg ${
                            isAlive ? 'bg-blue-50 border-blue-200' : 
                            isError ? 'bg-red-50 border-red-200' : 
                            isStatusResponse ? 'bg-green-50 border-green-200' : 
                            isAdminRsp ? 'bg-yellow-50 border-yellow-200' :
                            'bg-gray-50'
                          }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                isError ? 'destructive' : 
                                isStatusResponse ? 'default' : 
                                isAdminRsp ? 'secondary' :
                                isAlive ? 'secondary' : 
                                'default'
                              }>
                                {isAlive ? '💓 Alive' : 
                                 isAdminRsp ? (msg.rslt === 0 ? '✅ Ack' : '❌ Error') :
                                 isStatusResponse ? '✅ Status' : 
                                 msg.type || msg.command || msg.msg || 'message'}
                          </Badge>
                              {isAlive && msg.ver && (
                                <span className="text-xs text-gray-600">
                                  v{msg.ver}
                                </span>
                              )}
                              {isAdminRsp && (
                                <span className="text-xs text-gray-600">
                                  Seq: {msg.seq}, Result: {msg.rslt === 0 ? 'Success' : `Error ${msg.rslt}`}
                                </span>
                              )}
                            </div>
                          <span className="text-xs text-gray-500">
                            {new Date(msg.receivedAt || msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                          {isAlive ? (
                            <div className="text-xs space-y-1">
                              <div className="grid grid-cols-2 gap-2">
                                <div><strong>MAC:</strong> {msg.gmac || 'N/A'}</div>
                                <div><strong>Version:</strong> {msg.ver || 'N/A'}</div>
                                {msg.pubadmin && <div><strong>Pub Admin:</strong> <code className="text-xs">{msg.pubadmin}</code></div>}
                                {msg.subadmin && <div><strong>Sub Admin:</strong> <code className="text-xs">{msg.subadmin}</code></div>}
                              </div>
                              <details className="mt-2">
                                <summary className="cursor-pointer text-gray-600 hover:text-gray-800">View full message</summary>
                                <pre className="text-xs bg-white p-2 rounded overflow-x-auto mt-1">
                                  {JSON.stringify(msg, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : isAdminRsp ? (
                            <div className="text-xs space-y-1">
                              <div className="grid grid-cols-2 gap-2">
                                <div><strong>Type:</strong> {msg.stype || 'N/A'}</div>
                                <div><strong>Sequence:</strong> {msg.seq || 'N/A'}</div>
                                <div><strong>Result:</strong> {msg.rslt === 0 ? '✅ Success' : `❌ Error ${msg.rslt}`}</div>
                                {msg.gmac && <div><strong>Gateway MAC:</strong> {msg.gmac}</div>}
                              </div>
                              <details className="mt-2">
                                <summary className="cursor-pointer text-gray-600 hover:text-gray-800">View full message</summary>
                                <pre className="text-xs bg-white p-2 rounded overflow-x-auto mt-1">
                                  {JSON.stringify(msg, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                        <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
                          {JSON.stringify(msg, null, 2)}
                        </pre>
                          )}
                      </div>
                      )
                    })}
                  </div>
                )}
              </Tile>
          </TabsContent>
          )}

          {/* History Tab */}
          {!configOnly && (
          <TabsContent value="history">
            <Tile className="space-y-3 border border-[#e0e0e0] bg-white p-4">
              <div>
                <h3 className="text-base font-semibold text-[#161616]">Command History</h3>
                <p className="text-sm text-[#525252]">
                  Recently sent commands
                </p>
              </div>
                {commandHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No commands sent yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {commandHistory.map((cmd, idx) => (
                      <div
                        key={idx}
                        className="p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge>{cmd.command}</Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(cmd.sentAt).toLocaleString()}
                          </span>
                        </div>
                        <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
                          {JSON.stringify(cmd.payload, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </Tile>
          </TabsContent>
          )}
        </Tabs>
      )}

      {/* Command Configuration Modal */}
      <Modal
        open={showCommandModal}
        modalHeading="Configure Admin Command"
        primaryButtonText="Send Command"
        secondaryButtonText="Cancel"
        onRequestSubmit={async () => {
          if (!connected) {
            toast({
              title: 'Error',
              description: 'Please connect to the gateway MQTT broker first',
              variant: 'destructive'
            })
            return
          }

          if (!selectedCommandType) {
            toast({
              title: 'Error',
              description: 'Please select a command type',
              variant: 'destructive'
            })
            return
          }

          let payload = { ...commandConfig }
          
          // Build payload based on command type
          switch (selectedCommandType) {
            case 'set_upload_interval':
            case 'set_rssi_filter':
            case 'set_mac_filter':
            case 'set_name_filter':
            case 'set_beacon_types':
            case 'set_duplicate_filter':
              // For config commands, parse the JSON and use it directly as the command
              try {
                const configPayload = JSON.parse(commandConfig.config || commandJsonPayload)
                // Add sequence number if not present
                if (!configPayload.seq) {
                  configPayload.seq = Date.now()
                }
                // Add authentication if not present
                if (!configPayload.user) {
                  configPayload.user = 'admin'
                }
                if (!configPayload.pwd) {
                  configPayload.pwd = 'admin'
                }
                // Send the entire JSON as the command (not wrapped in payload)
                await sendAdminCommand('config', configPayload)
                setShowCommandModal(false)
                setSelectedCommandType('')
                setCommandConfig({})
                setCommandJsonPayload('')
                return
              } catch (e) {
                toast({
                  title: 'Error',
                  description: `Invalid JSON: ${e.message}`,
                  variant: 'destructive'
                })
                return
              }
            case 'reboot':
              // Reboot command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'reboot',
                seq: commandSequence,
                delay: parseInt(commandConfig.delay) || 5,
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'factory_reset':
              // Factory reset command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'factory',
                seq: commandSequence,
                confirm: 1,
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'change_password':
              if (!commandConfig.new_password || commandConfig.new_password.length < 5) {
                toast({
                  title: 'Error',
                  description: 'Password must be at least 5 characters',
                  variant: 'destructive'
                })
                return
              }
              // Password change command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'password',
                seq: commandSequence,
                oldPassword: commandConfig.old_password || '0000000000000000',
                newPassword: commandConfig.new_password,
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'update_network_config':
              // Network config command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'network',
                seq: commandSequence,
                ssid: commandConfig.ssid || 'YourWiFiName',
                password: commandConfig.password || 'YourWiFiPassword',
                encryption: commandConfig.encryption || 'psk2',
                dhcp: commandConfig.dhcp !== undefined ? commandConfig.dhcp : 1,
                staticIp: commandConfig.staticIp || '',
                staticMask: commandConfig.staticMask || '',
                staticGateway: commandConfig.staticGateway || '',
                staticDns1: commandConfig.staticDns1 || '',
                staticDns2: commandConfig.staticDns2 || '',
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'update_http_config':
              // HTTP config command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'http',
                seq: commandSequence,
                ctype: 'HTTP',
                httpUrl: commandConfig.httpUrl || 'https://your-api.com/gateway-data',
                httpHeader: commandConfig.httpHeader || 'Authorization: Bearer your_token',
                httpMaxPktSize: commandConfig.httpMaxPktSize || 100,
                httpPeriod: commandConfig.httpPeriod || '60',
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'configure_ap':
              // Access Point config command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'ap',
                seq: commandSequence,
                apSsid: commandConfig.apSsid || 'GatewayAP',
                apPassword: commandConfig.apPassword || 'password123',
                apHidden: commandConfig.apHidden !== undefined ? commandConfig.apHidden : 0,
                apPowerOff: commandConfig.apPowerOff !== undefined ? commandConfig.apPowerOff : 0,
                apLanIp: commandConfig.apLanIp || '192.168.8.1',
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'update_firmware':
              if (!commandConfig.url || !commandConfig.version) {
                toast({
                  title: 'Error',
                  description: 'Firmware URL and version are required',
                  variant: 'destructive'
                })
                return
              }
              // Firmware update command uses specific stype
              commandData = {
                msg: 'admin',
                stype: 'firmware',
                seq: commandSequence,
                fwUrl: commandConfig.url,
                fwVersion: commandConfig.version,
                fwChecksum: commandConfig.checksum || '',
                fwForce: commandConfig.force !== undefined ? commandConfig.force : 0,
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'update_ble_config':
              // BLE config command uses cfg stype with BLE parameters
              commandData = {
                msg: 'admin',
                stype: 'cfg',
                seq: commandSequence,
                actScan: commandConfig.actScan !== undefined ? commandConfig.actScan : '0',
                scanMode: commandConfig.scanMode !== undefined ? commandConfig.scanMode : '0',
                rssionly: commandConfig.rssionly !== undefined ? commandConfig.rssionly : '0',
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'update_mqtt_config':
              // MQTT config command uses cfg stype with MQTT parameters
              if (!commandConfig.murl || !commandConfig.username || !commandConfig.password) {
                toast({
                  title: 'Error',
                  description: 'MQTT broker URL, username, and password are required for MQTT config updates.',
                  variant: 'destructive'
                })
                return
              }
              commandData = {
                msg: 'admin',
                stype: 'cfg',
                seq: commandSequence,
                ctype: commandConfig.ctype || 'MQTT',
                murl: commandConfig.murl,
                mport: commandConfig.mport || '1883',
                username: commandConfig.username,
                password: commandConfig.password,
                clientid: commandConfig.clientid || '',
                pubtopic: commandConfig.pubtopic || '',
                subtopic: commandConfig.subtopic || '',
                pubaction: commandConfig.pubaction || '',
                subadmintopic: commandConfig.subadmintopic || '',
                pubadmintopic: commandConfig.pubadmintopic || '',
                qos: commandConfig.qos || '0',
                aliveTopic: commandConfig.aliveTopic || '0',
                user: 'admin',
                pwd: 'admin'
              }
              await sendAdminCommand('direct', commandData)
              setShowCommandModal(false)
              setSelectedCommandType('')
              setCommandConfig({})
              return
            case 'get_status':
              // For get_status, if user has modified the fetched config, send it as a config update
              if (commandConfig.config || commandJsonPayload) {
                try {
                  const configPayload = JSON.parse(commandConfig.config || commandJsonPayload)
                  // Add sequence number if not present
                  if (!configPayload.seq) {
                    configPayload.seq = Date.now()
                  }
                  // Send the entire JSON as a config command
                  await sendAdminCommand('config', configPayload)
                  setShowCommandModal(false)
                  setSelectedCommandType('')
                  setCommandConfig({})
                  setCommandJsonPayload('')
                  return
                } catch (e) {
                  toast({
                    title: 'Error',
                    description: `Invalid JSON: ${e.message}`,
                    variant: 'destructive'
                  })
                  return
                }
              } else {
                // If no config loaded, just fetch status (user should use Fetch button instead)
                payload = {}
              }
              break
          }

          await sendAdminCommand(selectedCommandType, payload)
          setShowCommandModal(false)
          setSelectedCommandType('')
          setCommandConfig({})
          setCommandJsonPayload('')
          setCurrentGatewayConfig(null)
        }}
        onRequestClose={() => {
          setShowCommandModal(false)
          setSelectedCommandType('')
          setCommandConfig({})
          setCommandJsonPayload('')
          setCurrentGatewayConfig(null)
        }}
        size="lg"
        className="gateway-admin-modal"
      >
        <div className="gateway-admin-modal__body space-y-4 p-4 text-[#161616]">
          <div>
            <Label className="mb-2">Command Type</Label>
            <div className="command-dropdown-wrapper gateway-admin-dropdown w-full min-w-0">
              <Dropdown
                id="modal-command-selector"
                title="Select Command"
                label="Select Command"
                items={commandOptionsForGateway}
                itemToString={(item) => (item ? item.text : '')}
                selectedItem={commandOptionsForGateway.find((opt) => opt.id === selectedCommandType) || null}
                onChange={({ selectedItem }) => {
                  if (selectedItem) {
                    setSelectedCommandType(selectedItem.id)
                    setCommandConfig({})

                    // Populate JSON payload template for config commands
                    if (COMMAND_TEMPLATES[selectedItem.id]) {
                      const template = COMMAND_TEMPLATES[selectedItem.id]
                      const jsonPayload = JSON.stringify(template, null, 2)
                      setCommandJsonPayload(jsonPayload)
                      setCommandConfig({ config: jsonPayload })
                    } else {
                      setCommandJsonPayload('')
                    }
                  }
                }}
                size="md"
              />
            </div>
          </div>

          {/* Command-specific fields */}
          {selectedCommandType === 'reboot' && (
            <div>
              <TextInput
                id="delay"
                labelText="Delay (seconds)"
                type="number"
                value={commandConfig.delay || '5'}
                onChange={(e) => setCommandConfig({ ...commandConfig, delay: e.target.value })}
                placeholder="5"
              />
            </div>
          )}

          {selectedCommandType === 'change_password' && (
            <div>
              <TextInput
                id="new_password"
                labelText="New Password (min 5 characters)"
                type="password"
                value={commandConfig.new_password || ''}
                onChange={(e) => setCommandConfig({ ...commandConfig, new_password: e.target.value })}
                placeholder="Enter new password"
              />
            </div>
          )}

          {selectedCommandType === 'update_firmware' && (
            <div className="space-y-3">
              <TextInput
                id="firmware_url"
                labelText="Firmware URL"
                value={commandConfig.url || ''}
                onChange={(e) => setCommandConfig({ ...commandConfig, url: e.target.value })}
                placeholder="https://example.com/firmware.bin"
              />
              <TextInput
                id="firmware_version"
                labelText="Version"
                value={commandConfig.version || ''}
                onChange={(e) => setCommandConfig({ ...commandConfig, version: e.target.value })}
                placeholder="2.1.0"
              />
              <TextInput
                id="firmware_checksum"
                labelText="Checksum (optional)"
                value={commandConfig.checksum || ''}
                onChange={(e) => setCommandConfig({ ...commandConfig, checksum: e.target.value })}
                placeholder="SHA256 checksum"
              />
            </div>
          )}

          {(selectedCommandType === 'set_upload_interval' ||
            selectedCommandType === 'set_rssi_filter' ||
            selectedCommandType === 'set_mac_filter' ||
            selectedCommandType === 'set_name_filter' ||
            selectedCommandType === 'set_beacon_types' ||
            selectedCommandType === 'set_duplicate_filter' ||
            selectedCommandType === 'update_ble_config' ||
            selectedCommandType === 'update_network_config' ||
            selectedCommandType === 'update_mqtt_config' ||
            selectedCommandType === 'update_http_config' ||
            selectedCommandType === 'configure_ap') && (
            <div>
              <Label className="mb-2">Command JSON Payload</Label>
              <Textarea
                id="config_json"
                className="w-full min-h-[250px] font-mono text-sm bg-gray-50 border-gray-300"
                value={commandConfig.config || commandJsonPayload}
                onChange={(e) => {
                  setCommandJsonPayload(e.target.value)
                  setCommandConfig({ ...commandConfig, config: e.target.value })
                }}
                placeholder='{"msg": "admin", "stype": "cfg", ...}'
              />
            </div>
          )}

          {selectedCommandType === 'factory_reset' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                ⚠️ WARNING: This will erase ALL gateway settings. This action cannot be undone.
              </AlertDescription>
            </Alert>
          )}

          {selectedCommandType === 'get_status' && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Fetch the current configuration from the gateway. The response will be displayed below.
                </p>
                <CarbonButton
                  kind="primary"
                  className="gateway-admin-action-btn w-full"
                  onClick={() => fetchCurrentConfig()}
                  disabled={loadingConfig || !selectedGateway}
                >
                  {loadingConfig ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Fetching Configuration...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Fetch Full Configuration (getCfg)
                    </>
                  )}
                </CarbonButton>
              </div>

              {gatewayCaps.usesAliveHeartbeat && gatewayLiveInfo && !currentGatewayConfig?.stype && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-amber-800 text-sm">
                    <strong>Admin topics not configured on gateway.</strong> The getCfg command cannot reach the device.
                    Below is partial config from the alive heartbeat. To enable full remote config, connect locally
                    and set Sub Admin Topic to <code className="bg-amber-100 px-1">{selectedGateway ? resolveGatewayMqttTopics(selectedGateway).subAdminTopic : 'lilygo/subadmin/MAC'}</code>
                  </AlertDescription>
                </Alert>
              )}

              {gatewayCaps.usesAliveHeartbeat && gatewayLiveInfo && !(commandConfig.config || commandJsonPayload) && (
                <CarbonButton
                  kind="tertiary"
                  size="sm"
                  onClick={() => {
                    const aliveData = JSON.stringify(currentGatewayConfig || gatewayLiveInfo, null, 2)
                    setCommandJsonPayload(aliveData)
                    setCommandConfig({ config: aliveData })
                  }}
                >
                  Load Available Config from Alive Message
                </CarbonButton>
              )}

              <div>
                <Label className="mb-2">Current Configuration (JSON)</Label>
                <Textarea
                  id="current_config_json"
                  className="w-full min-h-[300px] font-mono text-sm bg-gray-50 border-gray-300"
                  value={commandConfig.config || commandJsonPayload}
                  onChange={(e) => {
                    setCommandJsonPayload(e.target.value)
                    setCommandConfig({ ...commandConfig, config: e.target.value })
                  }}
                  placeholder='Click "Fetch Full Configuration" or "Load Available Config from Alive Message"...'
                  readOnly={loadingConfig}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}


